import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UUID } from '@4zen/api-client';
import { api } from '@/lib/api';

export const smartFlowKeys = {
  all: ['smart-flow'] as const,
  dayPlan: (date: string, userId?: UUID) => ['smart-flow', 'day-plan', date, userId ?? 'me'] as const,
};

export function useDayPlan(date: string, userId?: UUID) {
  return useQuery({
    queryKey: smartFlowKeys.dayPlan(date, userId),
    queryFn: () => api.smartFlow.today({ date, user_id: userId }),
    // The shortlist appears the moment the last task of the day is closed, so
    // keep this view fresher than the rest of the app.
    staleTime: 10_000,
  });
}

export function useRegenerateDayPlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { date?: string; user_id?: UUID }) => api.smartFlow.regenerate(body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: smartFlowKeys.all }),
  });
}

export function useReorderDayPlan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ planId, taskIds }: { planId: UUID; taskIds: UUID[] }) =>
      api.smartFlow.reorder(planId, taskIds),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: smartFlowKeys.all }),
  });
}

export function useAcceptSuggestion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ batchId, taskId }: { batchId: UUID; taskId: UUID }) =>
      api.smartFlow.acceptSuggestion(batchId, taskId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: smartFlowKeys.all });
      void queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

export function useDismissSuggestions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ batchId, reason }: { batchId: UUID; reason?: string }) =>
      api.smartFlow.dismissSuggestions(batchId, reason),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: smartFlowKeys.all }),
  });
}
